// 261010 Epic 2 / P5d — khung "Câu AI nhắc tới": đánh dấu (`<mark>`) câu AI trích trong prompt của video. Chỉ ĐỌC — không sửa prompt, không đụng nút sản xuất.
// Tìm theo hợp đồng BE (`_p5d/dev-be-1.md` §8): `snapshot.prompt.finalPrompt` của PHIẾU (đúng bản giám khảo đã thấy) → `storedPrompt` lúc chấm → prompt đang lưu hiện tại.
// Chỉ khi phiếu KHÔNG kèm snapshot mới hỏi bản gửi engine hiện tại (`fetchEngineNow` = GET engine-prompt có sẵn, chỉ đọc). Luôn ghi rõ đang hiện bản nào.
// Câu nằm ở phần kịch bản / shot / ảnh đính của `reviewInput` ⇒ chỉ hiện chữ (không phải lỗi). Không import antd (render được dưới jest). Không đoán gần đúng (xem promptEvidencePolicy.ts).
import React, { useEffect, useRef, useState } from 'react';
import type { PromptPick } from '../../types/filmStepReview';
import { findEvidence, needsEngineFetch } from './promptEvidencePolicy';

interface Props {
  pick: PromptPick;
  /** Prompt đang lưu hiện tại của video (`authorPrompt`). */
  savedPrompt: string;
  onClose: () => void;
  /** Lấy `finalPrompt` hiện tại của bản gửi engine. Chỉ gọi khi phiếu không có snapshot và prompt đang lưu không chứa câu trích. */
  fetchEngineNow?: () => Promise<string>;
}

type Now = { key: string; state: 'loading' | 'ready' | 'error'; text: string };

const PromptEvidencePanel: React.FC<Props> = ({ pick, savedPrompt, onClose, fetchEngineNow }) => {
  const local = findEvidence(pick, savedPrompt);
  const needNow = !local.hit && needsEngineFetch(pick) && !!fetchEngineNow;
  const key = `${pick.quote}\u0000${savedPrompt}`;
  const [now, setNow] = useState<Now | null>(null);
  const fetchRef = useRef(fetchEngineNow);
  fetchRef.current = fetchEngineNow;
  const markRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!needNow || !fetchRef.current) return undefined;
    let live = true; // đổi câu trích / prompt giữa chừng ⇒ kết quả trễ của lượt cũ bị bỏ
    setNow({ key, state: 'loading', text: '' });
    fetchRef.current()
      .then((t) => { if (live) setNow({ key, state: 'ready', text: t ?? '' }); })
      .catch(() => { if (live) setNow({ key, state: 'error', text: '' }); });
    return () => { live = false; };
  }, [needNow, key]);

  const cur: Now | null = needNow ? (now && now.key === key ? now : { key, state: 'loading', text: '' }) : null;
  const found = cur?.state === 'ready' ? findEvidence(pick, savedPrompt, cur.text) : local;
  const hit = found.hit;
  useEffect(() => { markRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); }, [hit]);

  let first = true;
  return (
    <section className="wf-rv-hl" aria-label="Câu AI nhắc tới trong prompt" data-testid="wf-rv-hl">
      <div className="wf-rv-hl-h">
        <b>Câu AI nhắc tới</b>
        <button type="button" className="wf-btn ghost" onClick={onClose}>Đóng</button>
      </div>
      {cur?.state === 'loading' ? <div className="wf-rv-note" role="status">Đang tải bản gửi engine hiện tại để tìm câu này…</div> : null}
      {hit ? (
        <>
          <div className="wf-rv-note" data-testid="wf-rv-hl-src">{hit.label}</div>
          <pre className="wf-pre" tabIndex={0} aria-label="Văn bản prompt, câu AI nhắc tới được đánh dấu">
            {hit.segments.map((s, i) => {
              if (!s.mark) return <React.Fragment key={i}>{s.text}</React.Fragment>;
              const ref = first ? markRef : undefined; first = false;
              return <mark key={i} ref={ref} data-testid="wf-rv-mark">{s.text}</mark>;
            })}
          </pre>
        </>
      ) : cur?.state === 'loading' ? null : (
        <>
          {cur?.state === 'error' ? <div className="wf-alert WARN" role="status">Không tải được bản gửi engine hiện tại nên chưa tìm được câu này trong đó.</div> : null}
          <div className="wf-rv-note" data-testid="wf-rv-hl-none">
            {found.inReviewInput
              ? 'Câu này nằm ở phần kịch bản / shot / ảnh đính của đề gửi giám khảo, không phải trong prompt — chỉ hiện chữ.'
              : 'Câu này không nằm trong prompt của phiếu này (kể cả bản đang lưu) — có thể prompt đã đổi sau lúc chấm.'}
          </div>
          <blockquote>{pick.quote}</blockquote>
        </>
      )}
    </section>
  );
};

export default PromptEvidencePanel;
